import { motion } from 'framer-motion';
import { WifiOff, RefreshCw, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * CabinErrorState — reusable error UI for all cabin pages.
 *
 * Props:
 *   onRetry     — function, triggers refetch (required)
 *   backPath    — string, path to navigate back to (optional; defaults to -1)
 *   title       — string, i18n key override (optional)
 *   message     — string, error message to display (optional)
 *   testId      — string, data-testid (optional)
 */
export default function CabinErrorState({
  onRetry,
  backPath,
  title,
  message,
  testId = 'cabin-error-state',
}) {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleBack = () => {
    if (backPath) {
      navigate(backPath);
    } else {
      navigate(-1);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="flex flex-col items-center gap-5 min-h-[60vh] justify-center px-6 py-12 text-center"
      data-testid={testId}
      aria-live="polite"
      role="alert"
    >
      {/* Icon */}
      <div className="w-20 h-20 rounded-3xl bg-rose-50 border-2 border-rose-200 flex items-center justify-center shadow-[0_4px_0_0_#FECACA]">
        <WifiOff size={36} className="text-rose-500" aria-hidden="true" strokeWidth={2} />
      </div>

      {/* Title */}
      <div className="max-w-xs">
        <h2 className="text-xl font-black text-[var(--text-main)] mb-2">
          {title || t('cabin.error.title')}
        </h2>
        <p className="text-sm text-[var(--text-muted)] leading-relaxed">
          {message || t('cabin.error.sub')}
        </p>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-center gap-3 w-full max-w-xs">
        {onRetry && (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            onClick={onRetry}
            className="flex-1 flex items-center justify-center gap-2 py-3 px-5 bg-[var(--brand-primary)] text-white font-bold rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] w-full"
            data-testid={`${testId}-retry`}
          >
            <RefreshCw size={16} aria-hidden="true" />
            {t('cabin.error.cta')}
          </motion.button>
        )}

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          onClick={handleBack}
          className="flex-1 flex items-center justify-center gap-2 py-3 px-5 bg-white border-2 border-gray-200 text-[var(--text-main)] font-bold rounded-xl hover:border-gray-300 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] w-full"
          data-testid={`${testId}-back`}
        >
          <ArrowLeft size={16} aria-hidden="true" />
          {t('common.go_back') || t('common.back')}
        </motion.button>
      </div>
    </motion.div>
  );
}
